import { useTranslation } from 'react-i18next';

type Point = { subKey: string; bodyKey: string; img: string };

const points: Point[] = [
  { subKey: 'philosophy.statement.sub1', bodyKey: 'philosophy.statement.para1', img: '/images/generated/skill-observation-2026.jpg' },
  { subKey: 'philosophy.statement.sub2', bodyKey: 'philosophy.statement.para2', img: '/images/generated/hero-butler-2026.jpg' },
  { subKey: 'philosophy.statement.sub3', bodyKey: 'philosophy.statement.para3', img: '/images/generated/corp-home-training-02.jpg' },
];

export default function PhilosophyStatement() {
  const { t } = useTranslation();

  return (
    <section className="relative py-24 md:py-32 bg-primary-900 overflow-hidden">
      <i
        aria-hidden="true"
        className="ri-double-quotes-l pointer-events-none absolute -top-8 left-2 md:left-10 text-accent-500/[0.06] text-[16rem] md:text-[22rem] leading-none"
      ></i>

      <div className="relative max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
        {/* Header + manifesto lead */}
        <div className="max-w-[820px] mb-16 md:mb-20">
          <p className="text-accent-400 text-[13px] tracking-[0.26em] mb-6 font-label">
            PHILOSOPHY
          </p>
          <h2 className="font-heading text-[28px] md:text-[40px] leading-[1.5] text-foreground-50 tracking-[0.06em]">
            {t('philosophy.statement.mainLine')}
          </h2>
          <div className="gold-line-left mt-8"></div>
        </div>

        {/* Alternating illustrated points */}
        <div className="space-y-14 md:space-y-20">
          {points.map((point, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div key={point.subKey} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
                <div className={`lg:col-span-6 ${reverse ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className="relative">
                    <div className="absolute -inset-2 border border-accent-500/20 pointer-events-none hidden md:block"></div>
                    <img
                      src={point.img}
                      alt={t(point.subKey)}
                      className="relative w-full aspect-[4/3] object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-6 ${reverse ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-8 h-px bg-accent-500/60"></span>
                    <span className="section-title-en text-accent-400/70 text-[13px] tracking-[0.16em]">0{idx + 1}</span>
                  </div>
                  <h3 className="font-heading text-[21px] md:text-[25px] text-foreground-50 tracking-[0.05em] leading-[1.6] mb-5">
                    {t(point.subKey)}
                  </h3>
                  {String(t(point.bodyKey)).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[16px] md:text-[17px] text-foreground-300 tracking-[0.03em] leading-[2.1]">{__s}</p>))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Example - gold-ruled pull quote */}
        <div className="mt-16 md:mt-20 max-w-[900px] mx-auto relative pl-6 md:pl-10 border-l-2 border-accent-500/50">
          <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-primary-900 border border-accent-500/40 flex items-center justify-center">
            <i className="ri-quote-text text-accent-400 text-xs"></i>
          </span>
          {String(t('philosophy.statement.example')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="font-heading text-[18px] md:text-[21px] text-foreground-100/90 tracking-[0.03em] leading-[2]">{__s}</p>))}
        </div>
      </div>
    </section>
  );
}
